import { FormattedMessage, useIntl } from 'react-intl';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { useModal } from 'mui-modal-provider';

import { useStyles } from './choose-service.styles';
import { ChooseServiceProps, ServiceButton } from './choose-service.models';
import { Page } from '../../page/page';
import fit from '../../../../../assets/images/ecp/fit.svg';
import buy from '../../../../../assets/images/ecp/buy.svg';
import cataract from '../../../../../assets/images/ecp/cataract.svg';
import checkUp from '../../../../../assets/images/ecp/check-up.svg';
import { EcpMap } from '../../ecp-map';
import { Icon, IconSizes, IconTypes } from '../../../../common';
import { InternalLinks } from '../../../../../constants';
import { EcpServices } from '../ecp-locator.models';
import { convertDateToUIString } from '../../../../../utils/date/convert-date-to-string';
import { ActiveAppointmentModal } from '../../active-appointment-modal';
import { AppointmentType } from '../../../../../graphql';
import { cx } from '../../../../../utils';

const buyButton: ServiceButton = {
  key: EcpServices.buy,
  label: <FormattedMessage id="ecp.services.buy" />,
  icon: buy,
  url: `${InternalLinks.ecpLocator}?ecpService=${EcpServices.buy}`,
  testId: 'ecp-buy-service-button', // Don't remove, used by YA analitics
};

const fitButton: ServiceButton = {
  key: EcpServices.fit,
  label: <FormattedMessage id="ecp.services.fit" />,
  icon: fit,
  url: `${InternalLinks.ecpLocator}?ecpService=${EcpServices.fit}`,
  testId: 'ecp-fit-service-button', // Don't remove, used by YA analitics
};

const fitLoadingButton: ServiceButton = {
  key: EcpServices.fit,
  label: <></>,
  icon: '',
  url: '',
  testId: 'ecp-fit-service-button-loader',
};

export const ChooseService = ({
  isCv,
  isAec,
  isFitted,
  cvBonusPoints,
  cvBonusEndDate,
  onCvDetailsClick,
  storeGroupCode,
  isCvCodeLoading,
  isLoggedIn,
  appointment,
}: ChooseServiceProps): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();
  const navigate = useNavigate();
  const { showModal } = useModal();

  const shouldDisplayCvService = isCv && isFitted;
  const hasStoreGroupCode = Boolean(storeGroupCode);
  const showCvButton = shouldDisplayCvService && hasStoreGroupCode;
  const showCvLoader = shouldDisplayCvService && isCvCodeLoading && !hasStoreGroupCode;
  const showFitLoader = appointment === undefined;

  const handleActiveAppointment = useCallback((date: string, type: AppointmentType): void => {
    showModal(ActiveAppointmentModal, {
      appointmentType: type,
      date,
      onView: () => navigate(InternalLinks.appointment),
    });
  }, [showModal, navigate]);

  const navigateToService = useCallback((url: string, key: EcpServices): void => {
    const isNoNavigation = isLoggedIn && !!appointment && !!appointment.date
      && (key === EcpServices.fit || key === EcpServices.cv || key === EcpServices.aec);

    if (isNoNavigation) {
      handleActiveAppointment(appointment.date, appointment.appointmentType);
    } else {
      navigate(url);
    }
  }, [navigate, appointment, isLoggedIn, handleActiveAppointment]);

  const buttons = useMemo(() => {
    const cataractButton: ServiceButton = {
      key: EcpServices.cataract,
      label: <FormattedMessage id="ecp.services.cataract" />,
      icon: cataract,
      url: `${InternalLinks.ecpLocator}?ecpService=${EcpServices.cataract}`,
      testId: 'ecp-cataract-service-button', // Don't remove, used by YA analitics
      isNew: !showCvButton,
    };

    const currentFitButton = showFitLoader ? fitLoadingButton : fitButton;

    if (showCvButton) {
      const cvButton: ServiceButton = {
        key: EcpServices.cv,
        label: <FormattedMessage id="common.cv.action" values={{ noBreak: <>&nbsp;</> }} />,
        icon: checkUp,
        url: `${InternalLinks.ecpLocator}?ecpService=${EcpServices.cv}&storeGroupCode=${storeGroupCode}`,
        testId: 'ecp-cv-service-button', // Don't remove, used by YA analitics
        bonusLabel: (cvBonusPoints && cvBonusEndDate) ? (
          <FormattedMessage
            id="ecp.services.controlVisit.bonusLabel"
            values={{
              points: cvBonusPoints,
              date: convertDateToUIString(cvBonusEndDate) ?? '',
            }}
          />
        ) : null,
        hasDetails: true,
        onDetailsClick: onCvDetailsClick,
      };
      return [buyButton, cvButton, currentFitButton, cataractButton];
    }
    if (showCvLoader) {
      const cvLoadingButton: ServiceButton = {
        key: EcpServices.cv,
        label: <></>,
        icon: '',
        url: '',
        testId: 'ecp-cv-service-button-loader',
      };

      return [buyButton, cvLoadingButton, currentFitButton, cataractButton];
    }
    switch (true) {
      case isAec:
        return [buyButton, currentFitButton, cataractButton];
      case isFitted:
        return [buyButton, currentFitButton, cataractButton];
      default:
        return [currentFitButton, buyButton, cataractButton];
    }
  }, [
    showCvButton, showCvLoader, showFitLoader, isAec, isFitted, cvBonusPoints, cvBonusEndDate,
    onCvDetailsClick, storeGroupCode,
  ]);

  return (
    <Page title={intl.formatMessage({ id: 'ecp.services.title' })}>
      <div className={classes.root}>
        <div data-testid="ecp-services" className={classes.services}>
          <h1
            className={classes.title}
            data-testid="ecp-services-title"
          >
            <FormattedMessage id="ecp.services.title" />
          </h1>
          <div className={classes.buttonsContainer}>
            {buttons.map(({
              key, label, icon, url, testId, isNew, bonusLabel, hasDetails, onDetailsClick,
            }) => ((key === EcpServices.cv && showCvLoader) || (key === EcpServices.fit && showFitLoader) ? (
              <div key={key} className={cx(classes.button, classes.loadingButton)} data-testid={`${key}-button-loader`}>
                <CircularProgress size={24} />
              </div>
            ) : hasDetails ? (
              <div key={key} className={cx(classes.button, classes.buttonWithDetails)}>
                <button
                  className={classes.cardOverlay}
                  onClick={(): void => navigateToService(url, key as unknown as EcpServices)}
                  aria-label={intl.formatMessage({ id: 'common.cv.action.ariaLabel' })}
                />
                <img src={icon} alt="" className={classes.icon} />
                <div className={classes.buttonMainRow}>
                  <span className={classes.labelText}>{label}</span>
                  {bonusLabel && (
                    <span className={classes.bonusLabel} data-testid={`${key}-bonus-label`}>
                      {bonusLabel}
                    </span>
                  )}
                  <span className={cx(classes.arrow, classes.arrowDetails)} />
                </div>
                <button
                  className={classes.detailsRow}
                  data-testid={`${key}-details`}
                  onClick={onDetailsClick}
                >
                  <span className={classes.detailsLabel}>
                    <FormattedMessage id="ecp.services.controlVisit.details" />
                  </span>
                  <Icon type={IconTypes.infoSmallBlue} size={IconSizes.xs} />
                </button>
              </div>
            ) : (
              <button
                key={key}
                className={classes.button}
                data-testid={testId}
                onClick={(): void => navigateToService(url, key as unknown as EcpServices)}
              >
                <img src={icon} alt="" className={classes.icon} />
                {label}
                {isNew && <span className={classes.label}><FormattedMessage id="ecp.services.new" /></span>}
                <span className={classes.arrow} />
              </button>
            )))}
          </div>
        </div>
        <div data-testid="ecp-map" className={cx(classes.map)}>
          <EcpMap ecpStores={[]} isMapHidden={false} storeDetail={null} isControlsHidden />
        </div>
      </div>
    </Page>
  );
};
